import type { LSLocation, LSFavouriteLocation } from '#shared/types/location';
import { useDefaultsStore } from '~/stores/useDefaultsStore';
import { useFavouriteLocationStore } from '../stores/useFavouriteLocationStore';

interface UseCombinedLocationsOptions {
	// Gates the app-boundary favourites fetch. Support portals refresh user
	// defaults (which carry localities) but have no consumer for saved locations,
	// so app.vue passes a `false` getter to stop the localities watch from firing
	// `getLocations_backend`. Defaults to enabled so the locations page keeps
	// auto-loading. Kept portal-agnostic: the app boundary owns the policy.
	autoFetch?: MaybeRefOrGetter<boolean>;
}

export const useCombinedLocations = (options?: UseCombinedLocationsOptions) => {
	const { autoFetch = true } = options ?? {};
	const defaults = useDefaultsStore();
	const favouriteStore = useFavouriteLocationStore();

	// Read cached location data directly — avoids creating a duplicate useAsyncData instance
	const { data: userLocationData } = useNuxtData<LSLocation[]>('lcp-locations');
	const locationState = useState<{ fetching: boolean }>('lcp-locations-state', () => ({
		fetching: false,
	}));

	const locations = useState<(LSLocation | LSFavouriteLocation)[]>(
		'lcp-combined-locations',
		() => [],
	);

	watchEffect(() => {
		const userLocations = userLocationData.value || [];
		const favouriteLocations = favouriteStore.allLocations.value;

		const seen = new Set<string>();
		const merged: (LSLocation | LSFavouriteLocation)[] = [];

		for (const loc of userLocations) {
			if (loc.locationKey) seen.add(loc.locationKey);
			merged.push(loc);
		}

		for (const loc of favouriteLocations) {
			if (loc.locationKey && seen.has(loc.locationKey)) continue;
			if (loc.locationKey) seen.add(loc.locationKey);
			merged.push(loc);
		}

		locations.value = merged;
	});

	const fetching = computed(() => locationState.value.fetching || favouriteStore.fetching.value);

	// Favourite locations are keyed by the account's localities (from user
	// defaults), so refetch them whenever those localities arrive or change. A
	// child-account swap clears and refetches defaults, which flows through here
	// to reload favourites for the new account. Replaces a one-shot onNuxtReady
	// fetch that never re-ran on a swap (and could miss the first load if defaults
	// had not resolved by the time the app became ready).
	watch(
		() => defaults.data.value?.localities,
		(localities) => {
			if (!toValue(autoFetch)) return;
			if (localities?.length) favouriteStore.fetchAll(localities);
		},
		{ immediate: true },
	);

	return {
		locations: readonly(locations),
		fetching,
	};
};
